import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/_atoms/button";
import { LoadingDots } from "@/components/_molecules/loading-dots/loading-dots";

import { reset2FAWithToken } from "../services/auth.service";

interface TwoFactorAuthResetFormProps {
  token: string;
}

export function TwoFactorAuthResetForm({ token }: TwoFactorAuthResetFormProps) {
  const { t } = useTranslation("auth");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    try {
      await reset2FAWithToken(token);
      setSuccess(true);
    } catch {
      setErrors([t("twoFactor.errors.generic")]);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex flex-col space-y-4 bg-background px-4 py-8 sm:px-16">
        <div className="text-center">
          <p className="text-sm text-green-500" role="status">
            {t("twoFactor.reset.successMessage")}
          </p>
        </div>
        <Button autoFocus onClick={() => navigate("/login")} className="w-full">
          {t("twoFactor.reset.continueToLogin")}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col space-y-4 bg-background px-4 py-8 sm:px-16">
      <div>
        <p className="mb-4 text-sm text-muted-foreground">
          {t("twoFactor.reset.description")}
        </p>
      </div>

      <div className="text-sm text-destructive">
        {errors.length > 0 && (
          <ul className="list-none">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        )}
      </div>

      <Button type="submit" disabled={loading} className="w-full">
        {loading ? <LoadingDots color="#808080" /> : <p>{t("twoFactor.reset.submitButton")}</p>}
      </Button>
    </form>
  );
}
